import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT, Environment, ValidationError } from '@netfoundry-ui/shared/model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { instantRange, likeContains } from './frontdoor-v3-execution.service';
import { TimeWindow } from './frontdoor-v3-list-filters';
import { checkFrontdoorBody } from './frontdoor-v3-validation';
import { LoggerService } from './logger.service';
import { HTTP_CLIENT } from './token.service';

/** An OIDC identity provider of a v3 frontdoor. HTTP shares name it in their login policy. The client secret is never returned. */
export interface OidcProviderV3 {
    [key: string]: any;

    id: string;
    frontdoorId: string;
    /** Unique within the account. */
    name: string;
    /** The ingress reads `<issuer>/.well-known/openid-configuration`. */
    issuer: string;
    clientId: string;
    /** Extra `aud` values a bearer token may carry, beside the client id. */
    audiences?: string[];
    /** Empty means the ingress default, `openid email profile`. */
    scopes?: string[];
    /** Absent means the ingress default, `groups`. */
    groupsClaim?: string;
    /** True when the ingress treats an email with no `email_verified` claim as verified. */
    trustMissingEmailVerified?: boolean;
    /** The prompt sent on login for every share that sets none itself. Absent sends no prompt; a read never carries `OMIT`. */
    prompt?: OidcPrompt;
    /** When the client secret last changed. */
    secretUpdatedAt?: string;
    /** The redirect URI to register at the provider: the environment's auth host callback, the same for every provider. */
    callbackUrl?: string;
    createdBy?: string;
    createdAt?: string;
    updatedBy?: string;
    updatedAt?: string;
}

/** A request to add a provider. The service fetches the issuer's discovery document before it accepts the create. */
export interface CreateOidcProviderV3 {
    name: string;
    issuer: string;
    clientId: string;
    clientSecret: string;
    audiences?: string[];
    scopes?: string[];
    groupsClaim?: string;
    /** Absent means false. */
    trustMissingEmailVerified?: boolean;
    /** Absent sends no prompt. */
    prompt?: OidcPrompt;
}

/**
 * The OIDC `prompt` the ingress sends when it starts a login. `OMIT` sends no prompt: on a share it turns off the provider's
 * prompt, and on a provider it is the same as no value.
 */
export type OidcPrompt = 'NONE' | 'LOGIN' | 'CONSENT' | 'SELECT_ACCOUNT' | 'OMIT';

/** The prompts a login can send, in the order the forms offer them. `param` is the OIDC `prompt` value the IdP gets. */
export const OIDC_PROMPTS: { value: Exclude<OidcPrompt, 'OMIT'>; param: string; title: string; detail: string }[] = [
    {
        value: 'LOGIN',
        param: 'login',
        title: 'Always log in',
        detail: 'The IdP asks for a login, even when the user has a session.',
    },
    {
        value: 'SELECT_ACCOUNT',
        param: 'select_account',
        title: 'Choose an account',
        detail: 'The IdP asks the user which account to use.',
    },
    {
        value: 'CONSENT',
        param: 'consent',
        title: 'Ask for consent',
        detail: 'The IdP asks the user to consent again.',
    },
    {
        value: 'NONE',
        param: 'none',
        title: 'Never show a login page',
        detail: 'Only a user with an IdP session gets in. Other users get a 403.',
    },
];

/** The title of `value`, "No prompt" for `OMIT`, or '' when there is no value to name. */
export function oidcPromptTitle(value: OidcPrompt | null | undefined): string {
    if (!value) {
        return '';
    }
    if (value === 'OMIT') {
        return 'No prompt';
    }
    return OIDC_PROMPTS.find((option) => option.value === value)?.title ?? value;
}

/** A `PATCH` body. A sent list replaces the stored one, an omitted secret keeps the stored one, and a null `groupsClaim` restores the default. */
export interface UpdateOidcProviderV3 {
    name?: string;
    issuer?: string;
    clientId?: string;
    clientSecret?: string;
    audiences?: string[];
    scopes?: string[];
    groupsClaim?: string | null;
    trustMissingEmailVerified?: boolean;
    /** Null or `OMIT` sends no prompt. */
    prompt?: OidcPrompt | null;
}

/** One page of providers, with the total across all pages. */
export interface OidcProviderPageV3 {
    oidcProviders: OidcProviderV3[];
    /** Total across all pages when the service reports one, otherwise null. */
    total: number | null;
    /** The redirect URI to register at every provider. An empty page carries it too. */
    callbackUrl?: string;
}

/** What a provider listing can be narrowed by at the service. */
export interface OidcProviderListFilters {
    /** A search box's value. It is sent as a `LIKE` contains-pattern, because the service adds no wildcards. */
    name?: string;
    /** Sent as a `LIKE` contains-pattern. */
    issuer?: string;
    /** Any of these. `OMIT` matches a provider that sends no prompt. */
    prompts?: OidcPrompt[];
    /** A scope the provider names, matched exactly. A provider on the default scopes does not match. */
    scope?: string;
    /** An audience the provider names, matched exactly. */
    audience?: string;
    trustMissingEmailVerified?: boolean;
    /** Sent as a `LIKE` contains-pattern. A provider on the default groups claim does not match. */
    groupsClaim?: string;
    secretUpdatedAt?: TimeWindow;
    createdAt?: TimeWindow;
    updatedAt?: TimeWindow;
}

export const DEFAULT_OIDC_PROVIDER_PAGE_SIZE = 50;

/** The query parameter on the OIDC Providers page that opens the detail drawer on one id. */
export const FD_OIDC_PROVIDER_QUERY_PARAM = 'oidcProvider';

/** The service's list limits. */
export const MAX_OIDC_PROVIDER_LIST_SIZE = 20;

/** Adds the filters a listing asks for. An absent or empty filter is left off. */
export function withOidcProviderFilters(params: HttpParams, filters: OidcProviderListFilters = {}): HttpParams {
    const texts: [key: string, value: string | undefined, pattern: boolean][] = [
        ['name', filters.name, true],
        ['issuer', filters.issuer, true],
        ['groupsClaim', filters.groupsClaim, true],
        ['scope', filters.scope, false],
        ['audience', filters.audience, false],
    ];
    const withTexts = texts.reduce((acc, [key, value, pattern]) => {
        const trimmed = (value ?? '').trim();
        if (trimmed.length === 0) {
            return acc;
        }
        return acc.set(key, pattern ? likeContains(trimmed) : trimmed);
    }, params);
    // Repeated rather than joined, because each parameter binds one value of a set.
    const withPrompts = (filters.prompts ?? []).reduce((acc, prompt) => acc.append('prompt', prompt), withTexts);
    const withTrust =
        filters.trustMissingEmailVerified === undefined
            ? withPrompts
            : withPrompts.set('trustMissingEmailVerified', String(filters.trustMissingEmailVerified));
    return (['secretUpdatedAt', 'createdAt', 'updatedAt'] as const).reduce((acc, field) => {
        const range = instantRange(filters[field]?.from, filters[field]?.to);
        return range ? acc.set(field, range) : acc;
    }, withTrust);
}

/** Reads and writes the OIDC Providers of one v3 frontdoor. Every path is scoped by the frontdoor id. */
@Injectable({ providedIn: 'root' })
export class FrontdoorV3OidcProviderService {
    private readonly apiUrl: string;

    constructor(
        private logger: LoggerService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        this.apiUrl = this.environment.frontdoorURL;
    }

    /** One page of `frontdoorId`'s providers. The service filters the rows to what the caller can read. */
    public findOidcProviderPage(
        frontdoorId: string,
        sort = 'name,ASC',
        size = DEFAULT_OIDC_PROVIDER_PAGE_SIZE,
        filters: OidcProviderListFilters = {},
        page = 0
    ): Observable<OidcProviderPageV3> {
        const params = withOidcProviderFilters(
            new HttpParams().set('sort', sort).set('size', String(size)).set('page', String(page)),
            filters
        );
        return this.http
            .get(this.providersUrl(frontdoorId), { headers: this.headers(), params })
            .pipe(map((response: any) => this.readPage(response)));
    }

    public getOidcProvider(frontdoorId: string, id: string): Observable<OidcProviderV3> {
        return this.http
            .get(this.providerUrl(frontdoorId, id), { headers: this.headers() })
            .pipe(map((response: any) => readProvider(response)));
    }

    /** The redirect URI to register at a provider. It reads a one-row page, which carries the URI also when the account has no provider. */
    public findOidcCallbackUrl(frontdoorId: string): Observable<string> {
        return this.findOidcProviderPage(frontdoorId, 'name,ASC', 1).pipe(map((page) => page.callbackUrl ?? ''));
    }

    /** Adds a provider. The body carries no frontdoor id, because the service names the account by the path alone. */
    public createOidcProvider(frontdoorId: string, create: CreateOidcProviderV3): Observable<OidcProviderV3> {
        return this.http
            .post(this.providersUrl(frontdoorId), create, { headers: this.headers() })
            .pipe(map((response: any) => readProvider(response)));
    }

    /** Whether {@link createOidcProvider} would be accepted. The dry run does not fetch the discovery document; the create does. */
    public validateCreateOidcProvider(
        frontdoorId: string,
        create: CreateOidcProviderV3
    ): Observable<ValidationError[]> {
        return checkFrontdoorBody(this.http, this.providersUrl(frontdoorId), create);
    }

    /** Applies `update` with a `PATCH`. The shares that use the provider follow the change within seconds. */
    public updateOidcProvider(
        frontdoorId: string,
        id: string,
        update: UpdateOidcProviderV3
    ): Observable<OidcProviderV3> {
        return this.http
            .patch(this.providerUrl(frontdoorId, id), update, { headers: this.headers() })
            .pipe(map((response: any) => readProvider(response)));
    }

    /** Whether {@link updateOidcProvider} would be accepted. Uses `PATCH`, the same method as the write. */
    public validateUpdateOidcProvider(
        frontdoorId: string,
        id: string,
        update: UpdateOidcProviderV3
    ): Observable<ValidationError[]> {
        return checkFrontdoorBody(this.http, this.providerUrl(frontdoorId, id), update, 'PATCH');
    }

    /** Deletes the provider and its secret. The service answers 409, naming the shares, while any share uses it. */
    public deleteOidcProvider(frontdoorId: string, id: string): Observable<any> {
        return this.http.delete(this.providerUrl(frontdoorId, id), { headers: this.headers() });
    }

    private providersUrl(frontdoorId: string): string {
        return `${this.apiUrl}/v3/${frontdoorId}/oidc-providers`;
    }

    private providerUrl(frontdoorId: string, id: string): string {
        return `${this.providersUrl(frontdoorId)}/${id}`;
    }

    private headers(): HttpHeaders {
        return new HttpHeaders().set('Accept', 'application/hal+json');
    }

    private readPage(response: any): OidcProviderPageV3 {
        const embedded = response?._embedded;
        const providers = embedded?.oidcProviders ?? (embedded ? Object.values(embedded)[0] : undefined);
        if (embedded && !Array.isArray(providers)) {
            this.logger.error('findOidcProviderPage: unrecognised response shape', response);
        }
        const rows = Array.isArray(providers) ? providers.map(readProvider) : [];
        const total = Number.isFinite(response?.page?.totalElements) ? response.page.totalElements : null;
        return { oidcProviders: rows, total, callbackUrl: callbackHref(response) };
    }
}

/** A provider with its `callbackUrl` link copied to a field, so pages read it like a share's. */
function readProvider(response: any): OidcProviderV3 {
    return { ...response, callbackUrl: callbackHref(response) };
}

function callbackHref(response: any): string | undefined {
    return response?._links?.callbackUrl?.href || undefined;
}
